import { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react'

interface LoadingContextState {
  loadedCards: Set<number>
  totalCards: number
  allLoaded: boolean
  loadingPercentage: number
  loadingComplete: boolean
  isTransitioning: boolean
  markCardAsLoaded: (contentIndex: number) => void
  setTotalCards: (total: number) => void
  setIsTransitioning: (transitioning: boolean) => void
}

const LoadingContext = createContext<LoadingContextState | null>(null)

export const useLoadingContext = () => {
  const context = useContext(LoadingContext)
  if (!context) {
    throw new Error('useLoadingContext must be used within LoadingProvider')
  }
  return context
}

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [loadedCards, setLoadedCards] = useState<Set<number>>(new Set())
  const [totalCards, setTotalCards] = useState(0)
  const [smoothPercentage, setSmoothPercentage] = useState(0)
  const [fakePercentage, setFakePercentage] = useState(0)
  const [smoothRealPercentage, setSmoothRealPercentage] = useState(0)
  const [hasReached100, setHasReached100] = useState(false)
  const [loadingComplete, setLoadingComplete] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const markCardAsLoaded = useCallback((contentIndex: number) => {
    setLoadedCards((prev) => {
      const newSet = new Set(prev)
      newSet.add(contentIndex)
      return newSet
    })
  }, [])

  const allLoaded = totalCards > 0 && loadedCards.size >= totalCards
  const rawRealPercentage = totalCards > 0 ? Math.round((loadedCards.size / totalCards) * 50) : 0
  const targetRealPercentage = allLoaded ? 100 : rawRealPercentage
  const targetPercentage = allLoaded ? 100 : Math.min(100, smoothRealPercentage + fakePercentage)

  useEffect(() => {
    if (targetRealPercentage === smoothRealPercentage) return

    const startPercentage = smoothRealPercentage
    const difference = targetRealPercentage - startPercentage
    const duration = 2000
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      const easeOut = 1 - Math.pow(1 - progress, 3)
      const currentPercentage = startPercentage + difference * easeOut

      setSmoothRealPercentage(Math.round(currentPercentage))

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    const animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [targetRealPercentage, smoothRealPercentage])

  useEffect(() => {
    if (fakePercentage >= 50 || allLoaded) return

    const interval = setInterval(() => {
      setFakePercentage((prev) => {
        const next = prev + 1
        return next >= 50 ? 50 : next
      })
    }, 1500)

    return () => clearInterval(interval)
  }, [fakePercentage, allLoaded])

  useEffect(() => {
    if (targetPercentage === smoothPercentage) return

    const startPercentage = smoothPercentage
    const difference = targetPercentage - startPercentage

    const isCompletionJump = targetPercentage === 100 && !hasReached100
    const duration = isCompletionJump ? 800 : 300

    if (isCompletionJump) {
      setHasReached100(true)
    }

    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      const easeOut = 1 - Math.pow(1 - progress, 3)
      const currentPercentage = startPercentage + difference * easeOut

      setSmoothPercentage(Math.round(currentPercentage))

      if (progress >= 1 && targetPercentage === 100) {
        setTimeout(() => {
          setLoadingComplete(true)
        }, 400)
      }

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    const animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [targetPercentage, smoothPercentage, hasReached100])

  const loadingPercentage = smoothPercentage

  const value = {
    loadedCards,
    totalCards,
    allLoaded,
    loadingPercentage,
    loadingComplete,
    isTransitioning,
    markCardAsLoaded,
    setTotalCards,
    setIsTransitioning,
  }

  return <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
}
